"use client"

import React from "react"
import { Popover, Transition } from "@headlessui/react"
import clsx from "clsx"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Fragment } from "react"
import { Button } from "../ui/button"
import { Container } from "./components/Container"
import { NavLink } from "./components/NavLink"
import { Logo } from "../ui/Logo"

function MobileNavLink({
  href,
  children
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <Popover.Button
      as={Link}
      href={href}
      className="block w-full rounded-lg px-3 py-2.5 text-gray-200 transition-colors hover:bg-white/5"
    >
      {children}
    </Popover.Button>
  )
}

function MobileNavIcon({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 overflow-visible stroke-gray-400"
      fill="none"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <title>Menu toggle icon</title>
      <path
        d="M0 1H14M0 7H14M0 13H14"
        className={clsx(
          "origin-center transition",
          open && "scale-90 opacity-0"
        )}
      />
      <path
        d="M2 2L12 12M12 2L2 12"
        className={clsx(
          "origin-center transition",
          !open && "scale-90 opacity-0"
        )}
      />
    </svg>
  )
}

function MobileNavigation({ loggedIn = false }: { loggedIn?: boolean }) {
  return (
    <Popover>
      <Popover.Button
        className="relative z-10 flex h-8 w-8 items-center justify-center [&:not(:focus-visible)]:focus:outline-none"
        aria-label="Toggle Navigation"
      >
        {({ open }) => <MobileNavIcon open={open} />}
      </Popover.Button>
      <Transition.Root>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Popover.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm" />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="duration-150 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-100 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel
            as="div"
            className="absolute inset-x-0 top-full mt-4 flex origin-top flex-col rounded-2xl border border-gray-800 bg-gray-950 p-4 text-base tracking-tight shadow-2xl"
          >
            <MobileNavLink href="/#features">Features</MobileNavLink>
            <MobileNavLink href="/#pricing">Pricing</MobileNavLink>
            <MobileNavLink href="/#faq">FAQ</MobileNavLink>
            <MobileNavLink href="/docs">Docs</MobileNavLink>
            <MobileNavLink href="/blog">Blog</MobileNavLink>
            <div className="my-2 h-px bg-gray-800" />
            {loggedIn ? (
              <MobileNavLink href="/dashboard">
                Go to Dashboard
              </MobileNavLink>
            ) : (
              <>
                <MobileNavLink href="/auth/sign-in">Sign in</MobileNavLink>
                <MobileNavLink href="/auth/sign-up">Sign up</MobileNavLink>
              </>
            )}
          </Popover.Panel>
        </Transition.Child>
      </Transition.Root>
    </Popover>
  )
}

export function Header({ loggedIn = false }: { loggedIn?: boolean }) {
  const router = useRouter()

  return (
    <header className="sticky top-0 z-50 border-b border-gray-800 bg-gray-950/80 backdrop-blur-xl">
      <Container>
        <nav className="relative z-50 flex items-center justify-between py-4">
          <div className="flex items-center md:gap-x-12">
            <Link
              href="/"
              className="inline-flex items-center"
              aria-label="Home"
            >
              <Logo variant="wordmark" />
            </Link>
            <div className="hidden md:flex md:gap-x-6">
              <NavLink href="/#features">Features</NavLink>
              <NavLink href="/#pricing">Pricing</NavLink>
              <NavLink href="/#faq">FAQ</NavLink>
              <NavLink href="/docs">Docs</NavLink>
              <NavLink href="/blog">Blog</NavLink>
            </div>
          </div>
          <div className="flex items-center gap-x-5 md:gap-x-8">
            <div className="-mr-1 md:hidden">
              <MobileNavigation loggedIn={loggedIn} />
            </div>
            {!loggedIn && (
              <div className="hidden items-center gap-x-5 md:flex md:gap-x-8">
                <NavLink href="/auth/sign-in">Sign in</NavLink>
              </div>
            )}
            {loggedIn ? (
              <Button
                onClick={() => router.push("/dashboard")}
                variant="solidBlue"
              >
                Go to Dashboard
              </Button>
            ) : (
              <Button
                onClick={() => router.push("/auth/sign-up")}
                variant="solidBlue"
              >
                Get Started
              </Button>
            )}
          </div>
        </nav>
      </Container>
    </header>
  )
}
